import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getRecentFights, getTopVotedFights } from "../services/fightsAPI";
import { getTopFighters } from "../services/playersAPI";
import { FeaturedFightCard } from "../components/home/FeaturedFightCard";
import { RecentFightsList } from "../components/home/RecentFightsList";
import { TopVotedFights } from "../components/home/TopVotedFights";
import { TopFightersLeaderboard } from "../components/home/TopFightersLeaderboard";
import { ExploreSection } from "../components/home/ExploreSection";
import { ListenUpSection } from "../components/home/ListenUpSection";
import Loader from "../components/ui/loader";

const searchCategories = [
  { label: "Fights", value: "fights" },
  { label: "Players", value: "players" },
  { label: "Teams", value: "teams" },
  { label: "Leagues", value: "leagues" },
  { label: "Games", value: "games" },
];

export const Home = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [category, setCategory] = useState("fights");

  const { data: recentData, isLoading: recentLoading } = useQuery({
    queryKey: ["recentFights"],
    queryFn: () => getRecentFights(6),
  });

  const { data: topVotedData, isLoading: topVotedLoading } = useQuery({
    queryKey: ["topVotedFights"],
    queryFn: () => getTopVotedFights(5),
  });

  const { data: topFightersData, isLoading: topFightersLoading } = useQuery({
    queryKey: ["topFighters"],
    queryFn: () => getTopFighters(5),
  });

  const recentFights = recentData?.data || [];
  const topVotedFights = topVotedData?.data || [];
  const topFighters = topFightersData?.data || [];

  const featuredFight = recentFights[0] || null;
  const remainingFights = recentFights.slice(1);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    navigate(`/${category}?search=${encodeURIComponent(searchInput.trim())}`);
  };

  const isLoading = recentLoading || topVotedLoading || topFightersLoading;

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-flyers-black">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row items-center gap-3">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-4 py-2 rounded bg-flyers-charcoal text-white border border-flyers-dark-gray focus:ring-2 focus:ring-flyers-orange outline-none w-full sm:w-auto"
          >
            {searchCategories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search..."
            className="px-4 py-2 rounded outline-none focus:ring-2 focus:ring-flyers-orange bg-flyers-charcoal text-white border border-flyers-dark-gray placeholder-flyers-mid-gray w-full sm:flex-1"
          />
          <button
            type="submit"
            className="bg-flyers-orange text-white px-6 py-2 rounded hover:bg-flyers-orange-light transition-colors duration-150 font-heading font-bold uppercase tracking-wider w-full sm:w-auto"
          >
            Search
          </button>
        </form>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            {/* Featured Fight + Recent Fights */}
            <section className="space-y-6">
              <FeaturedFightCard fight={featuredFight} />
              <RecentFightsList fights={remainingFights} />
            </section>

            {/* Trending: Most Voted + Top Fighters */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TopVotedFights fights={topVotedFights} />
              <TopFightersLeaderboard players={topFighters} />
            </section>

            {/* Listen Up — Podcast Friends */}
            <ListenUpSection />

            {/* Explore */}
            <section>
              <ExploreSection />
            </section>
          </>
        )}
      </div>
    </div>
  );
};
